// src/pages/EmailMessages.jsx
import React, { useEffect, useState } from "react";

/**
 * Simple email template store with backend-first, localStorage fallback.
 * Supported types:
 *   quote
 *   invoice
 *   service-report
 */

const LS_KEYS = {
  quote: "jwcs.emailTemplate.quote",
  invoice: "jwcs.emailTemplate.invoice",
  "service-report": "jwcs.emailTemplate.serviceReport",
};

const DEFAULTS = {
  quote: `Hello,

Please find attached your quotation {{number}}.

Kind regards,`,

  invoice: `Hello,

Please find attached your invoice {{number}}.

Kind regards,`,

  "service-report": `Hello,

Please find attached your service report {{number}}.

Kind regards,`,
};

async function fetchTemplate(type) {
  try {
    const res = await fetch(`/api/email-templates?type=${encodeURIComponent(type)}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    return (json && json.message) || DEFAULTS[type];
  } catch {
    return localStorage.getItem(LS_KEYS[type]) || DEFAULTS[type];
  }
}

async function saveTemplate(type, message) {
  try {
    const res = await fetch(`/api/email-templates`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ type, message }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return true;
  } catch {
    localStorage.setItem(LS_KEYS[type], message);
    return true;
  }
}

export default function EmailMessages() {
  const [type, setType] = useState("quote");
  const [message, setMessage] = useState(DEFAULTS.quote);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");

  useEffect(() => {
    let ignore = false;
    (async () => {
      setStatus("Loading…");
      const m = await fetchTemplate(type);
      if (!ignore) {
        setMessage(m);
        setStatus("");
      }
    })();
    return () => {
      ignore = true;
    };
  }, [type]);

  async function handleSave() {
    setSaving(true);
    setStatus("");
    try {
      await saveTemplate(type, message);
      setStatus("Saved ✓");
      setTimeout(() => setStatus(""), 3000);
    } catch (e) {
      setStatus(`Failed: ${e.message || e}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card" style={{ maxWidth: 900 }}>
      <h2>Email messages</h2>

      <p className="muted" style={{ marginTop: -6 }}>
        Define the default email body sent with Quotes, Invoices and Service Reports.
        You can use placeholders:{" "}
        <code>{"{{number}}"}</code>, <code>{"{{customer}}"}</code>, <code>{"{{company}}"}</code>.
      </p>

      <div
        style={{
          marginTop: 12,
          padding: 12,
          border: "1px solid var(--border)",
          borderRadius: 12,
          background: "var(--card)",
        }}
      >
        <div style={{ display: "grid", gap: 12, gridTemplateColumns: "220px 1fr" }}>
          <div>
            <label className="label">Template for</label>
            <select
              className="input"
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              <option value="quote">Quote</option>
              <option value="invoice">Invoice</option>
              <option value="service-report">Service Report</option>
            </select>
          </div>

          <div style={{ gridColumn: "1 / -1" }}>
            <label className="label">Message</label>
            <textarea
              className="input"
              rows={12}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={DEFAULTS[type]}
              style={{ fontFamily: "inherit", whiteSpace: "pre-wrap" }}
            />
          </div>

          <div style={{ gridColumn: "1 / -1", display: "flex", gap: 8 }}>
            <button className="btn" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </button>
            {status && (
              <div
                className="muted"
                style={{
                  alignSelf: "center",
                  color: status.startsWith("Saved") ? "#15803d" : undefined,
                }}
              >
                {status}
              </div>
            )}
          </div>
        </div>

        <div style={{ marginTop: 16 }} className="muted">
          <strong>Placeholders</strong>
          <br />
          <code>{"{{number}}"}</code> → Quote/Invoice/Report ID or number  
          <br />
          <code>{"{{customer}}"}</code> → Customer name (if available)  
          <br />
          <code>{"{{company}}"}</code> → Your business name  
        </div>
      </div>
    </div>
  );
}
